import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { wizardQuestions, platforms as staticPlatforms, calculateSuitability } from '@/data/platforms';
import { analyticsApi, platformsApi } from '@/services/api';

const WizardContext = createContext(null);

export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};

export const WizardProvider = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0); // 0 = intro, 1-5 = questions, 6 = results
  const [answers, setAnswers] = useState({});
  const [direction, setDirection] = useState('forward'); // for animations
  const [results, setResults] = useState([]);
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [platforms, setPlatforms] = useState(staticPlatforms); // Start with static, update from API
  const [platformsLoading, setPlatformsLoading] = useState(true);
  
  const navigate = useNavigate();
  const location = useLocation();

  const totalQuestions = wizardQuestions.length;

  // Fetch platforms from API on mount
  useEffect(() => {
    const fetchPlatforms = async () => {
      setPlatformsLoading(true);
      try {
        const data = await platformsApi.getAll(true); // Only enabled platforms
        if (data && data.length > 0) {
          setPlatforms(data);
        }
        // If no data, keep using static platforms
      } catch (err) {
        console.error('Failed to fetch platforms from API, using static data:', err);
        // Keep using static platforms on error
      } finally {
        setPlatformsLoading(false);
      }
    };
    
    fetchPlatforms();
  }, []);

  // Persist comparison selections in sessionStorage
  useEffect(() => {
    const saved = sessionStorage.getItem('comparisonSelections');
    if (saved) {
      setSelectedForComparison(JSON.parse(saved));
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem('comparisonSelections', JSON.stringify(selectedForComparison));
  }, [selectedForComparison]);

  // Persist results
  useEffect(() => {
    const savedResults = sessionStorage.getItem('wizardResults');
    const savedAnswers = sessionStorage.getItem('wizardAnswers');
    if (savedResults && savedAnswers) {
      setResults(JSON.parse(savedResults));
      setAnswers(JSON.parse(savedAnswers));
    }
  }, []);

  useEffect(() => {
    if (results.length > 0) {
      sessionStorage.setItem('wizardResults', JSON.stringify(results));
      sessionStorage.setItem('wizardAnswers', JSON.stringify(answers));
    }
  }, [results, answers]);

  const goToNext = useCallback(() => {
    setDirection('forward');
    if (currentStep <= totalQuestions) {
      setCurrentStep(prev => prev + 1);
      // Scroll to top on mobile - instant for better UX
      window.scrollTo(0, 0);
    }
  }, [currentStep, totalQuestions]);

  const goToPrevious = useCallback(() => {
    setDirection('backward');
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      // Scroll to top on mobile
      window.scrollTo(0, 0);
    }
  }, [currentStep]);

  const goToStep = useCallback((step) => {
    setDirection(step > currentStep ? 'forward' : 'backward');
    setCurrentStep(step);
    // Scroll to top on mobile
    window.scrollTo(0, 0);
  }, [currentStep]);

  const setAnswer = useCallback((questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  }, []);

  const toggleMultiAnswer = useCallback((questionId, optionId) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      
      if (optionId === 'not-sure') {
        return {
          ...prev,
          [questionId]: current.includes('not-sure') ? [] : ['not-sure'],
        };
      }
      
      const filtered = current.filter(id => id !== 'not-sure');
      
      if (filtered.includes(optionId)) {
        return {
          ...prev,
          [questionId]: filtered.filter(id => id !== optionId),
        };
      } else {
        return {
          ...prev,
          [questionId]: [...filtered, optionId],
        };
      }
    });
  }, []);

  const calculateResults = useCallback(() => {
    const scoredPlatforms = platforms.map(platform => ({
      ...platform,
      ...calculateSuitability(platform, answers),
    }));

    scoredPlatforms.sort((a, b) => {
      const suitabilityOrder = { suitable: 0, caution: 1, 'not-recommended': 2 };
      if (suitabilityOrder[a.suitability] !== suitabilityOrder[b.suitability]) {
        return suitabilityOrder[a.suitability] - suitabilityOrder[b.suitability];
      }
      return b.percentage - a.percentage;
    });

    // Return ALL platforms that meet criteria (no artificial limit)
    setResults(scoredPlatforms);
    
    // Track wizard completion
    analyticsApi.track('wizard_complete', { 
      answers_count: Object.keys(answers).length,
      results_count: scoredPlatforms.length
    }).catch(() => {}); // Silent fail for analytics
    
    return scoredPlatforms;
  }, [answers, platforms]);

  const resetWizard = useCallback(() => {
    setCurrentStep(0);
    setAnswers({});
    setResults([]);
    setDirection('forward');
    setSelectedForComparison([]);
    sessionStorage.removeItem('wizardResults');
    sessionStorage.removeItem('wizardAnswers');
    sessionStorage.removeItem('comparisonSelections');
    // Scroll to top before navigating
    window.scrollTo(0, 0);
    navigate('/');
  }, [navigate]);

  const getCurrentQuestion = useCallback(() => {
    if (currentStep >= 1 && currentStep <= totalQuestions) {
      return wizardQuestions[currentStep - 1];
    }
    return null;
  }, [currentStep, totalQuestions]);

  const canProceed = useCallback(() => {
    if (currentStep === 0) return true;
    
    const question = getCurrentQuestion();
    if (!question) return true;
    
    const answer = answers[question.id];
    
    if (question.type === 'multi-select') {
      return answer && answer.length > 0;
    }
    
    return answer !== undefined && answer !== '';
  }, [currentStep, getCurrentQuestion, answers]);

  // Comparison selection management
  const toggleComparison = useCallback((platformId) => {
    setSelectedForComparison(prev => {
      if (prev.includes(platformId)) {
        return prev.filter(id => id !== platformId);
      }
      if (prev.length >= 3) {
        return [...prev.slice(1), platformId];
      }
      return [...prev, platformId];
    });
  }, []);

  const clearComparison = useCallback(() => {
    setSelectedForComparison([]);
  }, []);

  const navigateToComparison = useCallback(() => {
    if (selectedForComparison.length >= 2) {
      navigate(`/compare?platforms=${selectedForComparison.join(',')}`);
    }
  }, [selectedForComparison, navigate]);

  const navigateToPlatform = useCallback((platformId) => {
    navigate(`/platform/${platformId}`);
  }, [navigate]);

  const navigateToResults = useCallback(() => {
    navigate('/');
    setCurrentStep(totalQuestions + 1);
  }, [navigate, totalQuestions]);

  // Helper to get platform by ID from current platforms list
  const getPlatformById = useCallback((id) => {
    return platforms.find(p => p.id === id);
  }, [platforms]);

  const value = {
    currentStep,
    totalQuestions,
    answers,
    results,
    direction,
    selectedForComparison,
    platforms,
    platformsLoading,
    goToNext,
    goToPrevious,
    goToStep,
    setAnswer,
    toggleMultiAnswer,
    calculateResults,
    resetWizard,
    getCurrentQuestion,
    canProceed,
    toggleComparison,
    clearComparison,
    navigateToComparison,
    navigateToPlatform,
    navigateToResults,
    getPlatformById,
  };

  return (
    <WizardContext.Provider value={value}>
      {children}
    </WizardContext.Provider>
  );
};
